import type { CardDef, StageDef } from "@reagent/sim";
import { ALL_CARDS } from "./cards";
import { STARTER_DECK } from "./starterDeck";
import { validateCard, validateRecipe, validateStage } from "./schema";
import { ALL_RECIPES } from "./recipes";
import { ERA_1_POOL, validatePool } from "./pathways";
import { STAGE_00 } from "./stages/00-induction";
import { STAGE_DIAGNOSTIC_MAX_BOARD } from "./stages/diagnostic-max-board";

/**
 * @reagent/content — the game's reagents, recipes, cards and stages.
 *
 * Everything here is validated the moment this module loads. A stage with an
 * edge to a node that doesn't exist, a deck naming a card that was renamed, or
 * an objective nothing on the board can produce, fails at startup with a
 * message naming the mistake — rather than presenting a rig that quietly does
 * nothing on tick 1, which is what those errors look like otherwise.
 *
 * The engine deliberately depends on none of this: @reagent/sim defines the
 * shapes and ships only its own test fixture, so content can be rebalanced
 * without touching simulation code, and the simulation can be tested without
 * loading a single stage.
 */

for (const recipe of ALL_RECIPES) validateRecipe(recipe);
for (const card of ALL_CARDS) validateCard(card);

const cardIds = new Set(ALL_CARDS.map((c) => c.id));
if (cardIds.size !== ALL_CARDS.length) {
  throw new Error("duplicate card id in the catalogue");
}

// A hardware card's injectsCards entry that names an unknown card is dead on
// install; one naming a hazard card is worse — a hazard injected by neglect
// must never be laundered away by dismantling the unit that caused it (spec
// 0020's own "Hazard cards are never bound" constraint), so injectsCards
// pointing at one would be silently unbindable by construction.
const cardsById = new Map(ALL_CARDS.map((c) => [c.id, c]));
for (const card of ALL_CARDS) {
  for (const injectedId of Object.keys(card.injectsCards ?? {})) {
    const injected = cardsById.get(injectedId);
    if (!injected) {
      throw new Error(`${card.id} injectsCards names a card that does not exist: ${injectedId}`);
    }
    if (injected.category === "hazard") {
      throw new Error(`${card.id} injectsCards names a hazard card, which can never be bound: ${injectedId}`);
    }
  }
}

const unknownStarter = STARTER_DECK.filter((id) => !cardIds.has(id));
if (unknownStarter.length > 0) {
  throw new Error(`the starter deck names cards that do not exist: ${unknownStarter.join(", ")}`);
}

// The pool is validated here like every stage, so a malformed pathway fails at
// startup naming the module rather than at pick time.
validatePool(ERA_1_POOL, ALL_CARDS);

/** Cards keyed by id, which is the shape EngineDriver wants. */
export const CARDS: Record<string, CardDef> = Object.fromEntries(
  ALL_CARDS.map((card) => [card.id, card]),
);

/**
 * The induction board (M6). Reachable from the job sheet, never from a run: putting
 * the tutorial in a run would make failing it cost a run. It is validated exactly as
 * strictly, just not played for stakes.
 */
export const TUTORIAL_STAGE: StageDef = validateStage(STAGE_00, ALL_CARDS);

/**
 * Loadable from the job sheet. Era 1's ten line modules (spec 0037), which a run
 * composes into its three stages with `pairingStages` rather than playing as they
 * stand, and the board-size diagnostic.
 */
export const FREE_PLAY_STAGES: readonly StageDef[] = [
  ...ERA_1_POOL.pathways.flatMap((p) => [p.core.module, p.refined.module]).map((stage) => validateStage(stage, ALL_CARDS)),
  validateStage(STAGE_DIAGNOSTIC_MAX_BOARD, ALL_CARDS),
];

/** Everything loadable, tutorial first — what the job sheet lists. */
export const PLAYABLE_STAGES: readonly StageDef[] = [TUTORIAL_STAGE, ...FREE_PLAY_STAGES];

export function stageById(id: string): StageDef | null {
  return PLAYABLE_STAGES.find((s) => s.id === id) ?? null;
}

export { ALL_CARDS } from "./cards";
export { ALL_RECIPES } from "./recipes";
export { STARTER_DECK } from "./starterDeck";
export { ALL_SOLUTIONS, solutionFor, liveBuildCommand, type LiveBuildStep, type StagePlay, type StageSolution } from "./solutions";
export { cardSchema, recipeSchema, stageSchema, validateCard, validateRecipe, validateStage } from "./schema";
export { STAGE_00 } from "./stages/00-induction";
export { STAGE_DIAGNOSTIC_MAX_BOARD } from "./stages/diagnostic-max-board";
export { ERA_1_POOL, validatePool } from "./pathways";
export { composeLineModules, composeLineSolutions, pairingStages } from "./compose";
export type { EraPoolDef, PathwayDef, PathwayTierDef } from "./pathways";
